"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function useCounter(target: number, isActive: boolean, duration = 1400) {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!isActive || startedRef.current) return;
    startedRef.current = true;
    const startTime = performance.now();
    const easeOutExpo = (t: number) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutExpo(progress);
      setValue(Math.round(target * eased));
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, isActive, duration]);
  return value;
}
export default function Marketreach() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tile1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.4
  });
  const tile2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.4
  });
  const tile3 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.4
  });
  const count1 = useCounter(95, tile1.isVisible);
  const count2 = useCounter(40, tile2.isVisible);
  const count3 = useCounter(25, tile3.isVisible);
  const sparkPoints = "0,18 12,14 24,16 36,11 48,12 60,7 72,9 84,4 96,5";
  return <section data-section-id="12211" id="globalmarketreach" className="bg-white text-[#1a1a1a] py-28 md:py-40 px-5 md:px-8">
      <div className="max-w-[1320px] mx-auto">
        {}
        <div ref={header.ref} className="max-w-[720px] mb-16 transition-all duration-700" style={{
        opacity: header.isVisible ? 1 : 0,
        transform: header.isVisible ? "translateY(0)" : "translateY(24px)",
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
      }}>
          <h2 className="font-default font-semibold text-[#1a1a1a]" style={{
          fontSize: "clamp(2rem, 3.8vw, 3.5rem)",
          letterSpacing: "-0.025em",
          lineHeight: 1.05
        }}>
            {"Global market reach, verified."}
          </h2>
          <p className="mt-5 font-default font-normal" style={{
          fontSize: "clamp(1rem, 1.1vw, 1.0625rem)",
          lineHeight: 1.55,
          color: "#666666"
        }}>
            {"Proven trade metrics for partners utilizing the KTEX ecosystem."}
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {}
          <div ref={tile1.ref} data-index={0} className="md:col-span-5 group relative overflow-hidden border border-[#e0e0e0] bg-white transition-all duration-[320ms] ease-out hover:-translate-y-1" style={{
          borderRadius: "8px",
          padding: "clamp(28px, 3vw, 48px)",
          boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          opacity: tile1.isVisible ? 1 : 0,
          transform: tile1.isVisible ? "translateY(0)" : "translateY(24px)",
          transitionProperty: "opacity, transform, border-color, box-shadow",
          transitionDuration: "620ms, 620ms, 320ms, 320ms",
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1), cubic-bezier(0.22, 1, 0.36, 1), ease-out, ease-out"
        }} onMouseEnter={e => {
          e.currentTarget.style.borderColor = "#d4a574";
        }} onMouseLeave={e => {
          e.currentTarget.style.borderColor = "#e0e0e0";
        }}>
            <div className="absolute top-0 right-0 w-72 h-72 pointer-events-none" style={{
            background: "radial-gradient(circle at top right, #d4a574 / 0.05, transparent 70%)"
          }} aria-hidden="true" />
            <div className="relative">
              <div className="font-mono uppercase" style={{
              fontSize: "0.8125rem",
              letterSpacing: "0.04em",
              color: "#1a3a52",
              fontFamily: "var(--font-mono)"
            }}>
                {"SUPPLIER VERIFICATION"}
              </div>
              <div className="mt-6 font-default font-medium tabular-nums text-[#1a1a1a]" style={{
              fontSize: "clamp(3.5rem, 6vw, 5.5rem)",
              letterSpacing: "-0.04em",
              lineHeight: 1
            }}>
                {count1}
                <span style={{
                color: "#1a3a52"
              }}>{"%"}</span>
              </div>
              <p className="mt-6 font-default max-w-[28ch]" style={{
              fontSize: "1.0625rem",
              lineHeight: 1.55,
              color: "#666666"
            }}>
                {"Verified trade partners across 50+ countries."}
              </p>
            </div>
          </div>

          {}
          <div ref={tile2.ref} data-index={1} className="md:col-span-4 group relative overflow-hidden border border-[#e0e0e0] bg-white transition-all duration-[320ms] ease-out hover:-translate-y-1" style={{
          borderRadius: "8px",
          padding: "clamp(28px, 3vw, 44px)",
          boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          opacity: tile2.isVisible ? 1 : 0,
          transform: tile2.isVisible ? "translateY(0)" : "translateY(24px)",
          transitionProperty: "opacity, transform, border-color, box-shadow",
          transitionDuration: "620ms, 620ms, 320ms, 320ms",
          transitionDelay: "80ms, 80ms, 0ms, 0ms",
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1), cubic-bezier(0.22, 1, 0.36, 1), ease-out, ease-out"
        }} onMouseEnter={e => {
          e.currentTarget.style.borderColor = "#d4a574";
        }} onMouseLeave={e => {
          e.currentTarget.style.borderColor = "#e0e0e0";
        }}>
            <div className="font-mono uppercase" style={{
            fontSize: "0.8125rem",
            letterSpacing: "0.04em",
            color: "#1a3a52",
            fontFamily: "var(--font-mono)"
          }}>
              {"TRADE EFFICIENCY"}
            </div>
            <div className="mt-6 font-default font-medium tabular-nums text-[#1a1a1a]" style={{
            fontSize: "clamp(3rem, 5vw, 4.5rem)",
            letterSpacing: "-0.04em",
            lineHeight: 1
          }}>
              {count2}
              <span style={{
              color: "#1a3a52"
            }}>{"%"}</span>
            </div>
            <div className="mt-8">
              <svg viewBox="0 0 96 24" className="w-full h-10" preserveAspectRatio="none" aria-hidden="true">
                <polyline points={sparkPoints} fill="none" stroke="#1a3a52" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="96" cy="5" r="2" fill="#1a3a52" />
              </svg>
            </div>
          </div>

          {}
          <div ref={tile3.ref} data-index={2} className="md:col-span-3 group relative overflow-hidden border border-[#e0e0e0] bg-white transition-all duration-[320ms] ease-out hover:-translate-y-1" style={{
          borderRadius: "8px",
          padding: "clamp(28px, 3vw, 40px)",
          boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          opacity: tile3.isVisible ? 1 : 0,
          transform: tile3.isVisible ? "translateY(0)" : "translateY(24px)",
          transitionProperty: "opacity, transform, border-color, box-shadow",
          transitionDuration: "620ms, 620ms, 320ms, 320ms",
          transitionDelay: "160ms, 160ms, 0ms, 0ms",
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1), cubic-bezier(0.22, 1, 0.36, 1), ease-out, ease-out"
        }} onMouseEnter={e => {
          e.currentTarget.style.borderColor = "#d4a574";
        }} onMouseLeave={e => {
          e.currentTarget.style.borderColor = "#e0e0e0";
        }}>
            <div className="font-mono uppercase" style={{
            fontSize: "0.8125rem",
            letterSpacing: "0.04em",
            color: "#1a3a52",
            fontFamily: "var(--font-mono)"
          }}>
              {"RESPONSE TIME"}
            </div>
            <div className="mt-6 font-default font-medium tabular-nums text-[#1a1a1a] flex items-baseline" style={{
            fontSize: "clamp(3rem, 5vw, 4.5rem)",
            letterSpacing: "-0.04em",
            lineHeight: 1
          }}>
              <span style={{
              fontSize: "0.5em",
              marginRight: "0.1em",
              color: "#666666"
            }}>
                {"<"}
              </span>
              {count3}
            </div>
            <div className="mt-8 flex items-center justify-start">
              <div className="relative w-12 h-12">
                <span className="absolute inset-0 rounded-lg border" style={{
                borderColor: "#1a3a52",
                borderWidth: "1px",
                animation: "impact-pulse 2s ease-out infinite"
              }} />
                <span className="absolute inset-0 rounded-lg border" style={{
                borderColor: "#1a3a52",
                borderWidth: "1px",
                animation: "impact-pulse 2s ease-out infinite",
                animationDelay: "1s"
              }} />
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-lg" style={{
                backgroundColor: "#1a3a52"
              }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes impact-pulse {
          0% {
            transform: scale(0.6);
            opacity: 0.9;
          }
          100% {
            transform: scale(1.4);
            opacity: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes impact-pulse {
            0%, 100% { transform: scale(1); opacity: 0.5; }
          }
        }
      `}</style>
    </section>;
}